import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Bed,
  HelpCircle,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Search,

  Sun,
  User,
  X,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { useCommandPalette } from '../../hooks/useCommandPalette';
import { useTheme } from '../../hooks/useTheme';
import CommandPalette from '../ui/CommandPalette';
import AccessClosedNote from '../notices/AccessClosedNote';

const sidebarNavItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/student/dashboard' },
  { id: 'bookings', label: 'My Bookings', icon: Bed, href: '/student/bookings' },
  { id: 'support', label: 'Support', icon: HelpCircle, href: '/student/support' },
  { id: 'profile', label: 'Profile', icon: User, href: '/student/profile' },
];

const StudentLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { platformName, bookingWindow, loading: brandLoading } = useBrand();

  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const commandPalette = useCommandPalette();
  const { resolvedTheme, toggle: toggleTheme } = useTheme();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleNavClick = (item: (typeof sidebarNavItems)[number]) => {
    setSidebarOpen(false);
    navigate(item.href);
  };

  const getActiveId = () => {
    const path = location.pathname;
    if (path.startsWith('/student/bookings')) return 'bookings';
    if (path.startsWith('/student/support')) return 'support';
    if (path.startsWith('/student/profile')) return 'profile';
    return 'dashboard';
  };

  const activeId = getActiveId();

  if (!user) {
    return (
      <div className="min-h-screen booking-shell">
        <main className="mx-auto max-w-xl px-4 py-20">
          <section className="booking-glass rounded-2xl p-8 text-center animate-scale-in">
            <h1 className="text-2xl font-bold text-foreground">Student Sign In Required</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Please sign in to access your student portal.
            </p>
            <div className="mt-6 space-y-2">
              {!brandLoading && bookingWindow.signInOpen && (
                <Link
                  to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
                  className="block rounded-lg bg-foreground px-4 py-2.5 text-sm font-semibold text-background"
                >
                  Sign In
                </Link>
              )}
              {!brandLoading && bookingWindow.registrationOpen && (
                <Link
                  to={`/register?redirect=${encodeURIComponent(location.pathname)}`}
                  className="block rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-secondary-foreground"
                >
                  Create Account
                </Link>
              )}
              {!brandLoading &&
                !bookingWindow.signInOpen &&
                !bookingWindow.registrationOpen && <AccessClosedNote />}
              <Link
                to="/"
                className="block rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
              >
                Return to Home
              </Link>
            </div>
          </section>
        </main>
      </div>
    );
  }

  if (user.role !== 'STUDENT') {
    return (
      <div className="min-h-screen booking-shell">
        <main className="mx-auto max-w-xl px-4 py-20">
          <section className="booking-glass rounded-2xl p-8 text-center animate-scale-in">
            <h1 className="text-2xl font-bold text-foreground">Student Account Required</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This area is available only for student accounts.
            </p>
            <div className="mt-6 space-y-2">
              <Link
                to="/"
                className="block rounded-lg bg-foreground px-4 py-2.5 text-sm font-semibold text-background"
              >
                Go to Home
              </Link>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-muted">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-foreground focus:shadow-lg focus:ring-2 focus:ring-ring">
        Skip to main content
      </a>
      {/* Mobile Header */}
      <div className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b border-border bg-background px-4 py-3 lg:hidden">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
            <Home className="h-4 w-4 text-white" />
          </div>
          <span className="text-lg font-bold text-foreground">{platformName}</span>
        </Link>
        <div className="flex items-center gap-1">
          <button
            onClick={commandPalette.toggle}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
            aria-label="Open command palette"
          >
            <Search className="h-5 w-5" />
          </button>
          <button
            onClick={toggleTheme}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
            aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {resolvedTheme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-50 flex h-screen w-64 flex-col border-r border-border bg-background transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 px-6 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
            <Home className="h-5 w-5 text-white" />
          </div>
          <span
            className="text-xl font-bold text-foreground"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {platformName}
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4">
          {/* Search shortcut */}
          <button
            onClick={commandPalette.toggle}
            className="mb-3 flex w-full items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Search className="h-4 w-4" />
            <span className="flex-1 text-left text-xs">Search...</span>
            <kbd className="inline-flex h-5 items-center gap-0.5 rounded border border-border bg-background px-1.5 text-[10px] font-medium">
              &#8984;K
            </kbd>
          </button>
          <ul className="space-y-1">
            {sidebarNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeId === item.id;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavClick(item)}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-brand-subtle text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
                    />
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Theme toggle */}
        <div className="border-t border-border px-4 py-2">
          <button
            onClick={toggleTheme}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {resolvedTheme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            {resolvedTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>
        </div>

        {/* User Profile */}
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <span className="text-sm font-semibold text-amber-700">
                {user.firstName?.[0] || 'S'}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">
                {user.firstName || 'Student'} {user.lastName || ''}
              </p>
              <p className="text-xs text-muted-foreground">Student Member</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-muted-foreground transition-colors hover:text-foreground"
              title="Logout"
              aria-label="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main id="main-content" className="flex-1 p-4 pt-20 sm:p-6 sm:pt-20 lg:ml-64 lg:p-8 lg:pt-8">
        <Outlet />
      </main>

      <CommandPalette open={commandPalette.open} onClose={commandPalette.close} />
    </div>
  );
};

export default StudentLayout;
